/**
 * Whether this stage's pages may be indexed (VIT-17997).
 *
 * Deliberately plain TypeScript with no framework imports, for the same reason as
 * `app/content-security-policy.ts`: three very different consumers read it, and a preview stage
 * is only actually protected if all three agree.
 *
 *  - `app/entry.server.tsx` stamps `X-Robots-Tag` on SSR documents. This is the layer that
 *    covers the surface reviewers and crawlers actually see off-prod: PR bodies advertise
 *    `PreviewUrl`, which is the INNER `sst.aws.React` distribution (sst.config.ts), and that
 *    distribution has no response headers policy attached.
 *  - `app/root.tsx` renders `<meta name="robots">` in `Layout`. The prerendered pages
 *    (`/terms`, `/privacy`, `/support`, `/mcp-apps`, `/mcp-demo`) are static S3 objects that
 *    never invoke the server Lambda, so on the inner distribution NOTHING else reaches them —
 *    the exact gap `test/site.integration.test.ts` already documents for the CSP.
 *  - `sst.config.ts` adds the header to the shared CloudFront `ResponseHeadersPolicy` off-prod,
 *    which blankets the OUTER per-stage distribution: static assets, `/robots.txt`,
 *    `/sitemap.xml`, `/llms.txt`.
 *
 * Why a `noindex` directive rather than `Disallow: /` in a per-stage robots.txt: `Disallow`
 * blocks crawling, not indexing — a disallowed URL discovered elsewhere can still be indexed,
 * listed without a snippet, because the crawler never fetches the page and so never reads the
 * directive telling it to stay out. Crawlable-and-noindexed is the combination that actually
 * works, which is the same trade-off already reasoned out at `app/routes/resource.geo.ts:11`.
 *
 * The canonical (`app/seo.ts`) offers no protection here and is not asked to. It is absolute
 * since VIT-18029, so a preview names vit.al in its `<link rel=canonical>` exactly as it already
 * did in `og:url`, the JSON-LD nodes, the sitemap and llms.txt. Preview isolation rests entirely
 * and deliberately on the three `noindex, nofollow` layers above. Note that those do not stop a
 * preview being CRAWLED — being crawled is how the directive gets read at all, which is the whole
 * point of the `Disallow` reasoning above — but a page a crawler has been told not to index is
 * not a page it consolidates duplicates onto, so its canonical never gets acted on.
 */

/** The response header a robots directive travels in when there is no document to hold a meta tag. */
export const ROBOTS_TAG_HEADER = 'X-Robots-Tag'

/**
 * What an off-production stage says about itself.
 *
 * `nofollow` alongside `noindex` because a preview is a whole site, not a page. `noindex` alone
 * would already keep every URL out of the index — each link target is itself noindexed — but it
 * leaves a crawler free to walk the entire preview route tree first, spending crawl budget on a
 * throwaway stage and discovering more of its URLs to sit in Search Console as excluded. There
 * is nothing on a preview worth following. This matches what `app/routes/mcp-demo.tsx` already
 * says for the same reason.
 */
const OFF_PRODUCTION_DIRECTIVE = 'noindex, nofollow'

/**
 * The robots directive for a stage, or `undefined` when its pages should be indexed normally.
 *
 * Note the fail direction, which is deliberate and is the opposite of the CSP's. A missing or
 * empty `VITE_ENVIRONMENT` yields `undefined` — indexable — rather than a directive, so a stage
 * that somehow loses the variable degrades to today's behaviour instead of silently deindexing
 * vit.al. The two failures are not symmetric: an unprotected preview is only the status quo this
 * ticket set out to fix, while deindexing production is a catastrophe that takes weeks of
 * recrawling to undo. `app/content-security-policy.ts` defaults the other way because
 * mislabelling a violation report costs nothing.
 *
 * `stage` is the SST stage, which is what `VITE_ENVIRONMENT` carries (sst.config.ts) — set on
 * the SSR Lambda and in the build subprocess, so the prerendered pages bake the right answer.
 */
export const stageRobotsDirective = (stage: string | undefined): typeof OFF_PRODUCTION_DIRECTIVE | undefined =>
  stage != null && stage !== '' && stage !== 'production' ? OFF_PRODUCTION_DIRECTIVE : undefined
